import { giphyLightGrey, giphyPink, giphyRed } from '@giphy/js-brand'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import CreationToolOutputType from '../../../../../types/output-type'
import { SquareButton } from '../../../../buttons'
import { CreationToolUploadContext } from '../../../manager'
import { CreationToolUploadSource } from '../../../types'
import LabelInput from './input'

const Container = styled.div<{ editable: boolean }>`
    align-items: center;
    box-sizing: border-box;
    display: flex;
    font-size: 15px;
    height: 100%;
    line-height: 1;
    margin-left: 10px;
    margin-right: auto;
    min-width: 0; // this is needed for text truncating in flexbox
    pointer-events: ${({ editable }) => (editable ? 'auto' : 'none')};
    position: relative;
`

const Label = styled.span`
    color: #fff;
    font-weight: bold;
    margin-right: 5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const EditIcon = styled(SquareButton)`
    background: transparent;
    flex-shrink: 0;
    opacity: 0.65;

    &:hover {
        background: #000;
        opacity: 1;
    }
`

const Required = styled.span`
    background: linear-gradient(135deg, ${giphyPink} 0%, ${giphyRed} 100%);
    font-size: 13px;
    font-weight: bold;
    margin-right: 5px;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
`

type Props = {
    disabled?: boolean
    source: CreationToolUploadSource
    onEditing?: (isEditing: boolean) => void
}

const CreationUploadItemLabel = ({ source: uploadSource, disabled = false, onEditing }: Props) => {
    const { updateUploadSource } = useContext(CreationToolUploadContext)
    const [isEditing, setIsEditing] = useState<boolean>(false)
    const { outputType, title, source: src } = uploadSource
    const isEditable = !disabled && outputType === CreationToolOutputType.Video
    const isTitleRequired = isEditable && CreationToolOutputType.requiresTitle(outputType)
    const filename = typeof src.source === 'string' ? src.source : src.source.name
    const label = title || (isTitleRequired ? 'Add Title' : filename)

    const onTitleSave = (title: string) => {
        updateUploadSource({ ...uploadSource, title })
        setIsEditing(false)
    }

    useEffect(() => {
        onEditing?.(isEditing)
    }, [isEditing])

    const getLabelComponent = () => {
        // show the input component if editing
        if (isEditing) {
            return <LabelInput source={uploadSource} onSave={onTitleSave} />
        }

        // show the title with pencil icon for editing
        if (isEditable) {
            return (
                <>
                    <Label
                        style={{ cursor: 'pointer', color: isTitleRequired && !title ? giphyLightGrey : undefined }}
                        onClick={() => setIsEditing(true)}
                    >
                        {label}
                    </Label>
                    {isTitleRequired && !title && <Required onClick={() => setIsEditing(true)}>Required</Required>}
                    <EditIcon className="ss-write" onClick={() => setIsEditing(true)} />
                </>
            )
        }

        // show the plain ol' title
        return <Label>{label}</Label>
    }

    return (
        <Container editable={isEditable} style={isEditing ? { width: '100%' } : undefined}>
            {getLabelComponent()}
        </Container>
    )
}

export default CreationUploadItemLabel
